import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FacilityEntity, FacilityEntityType } from '@/types/facility';
import { ENTITY_RENDER_CONFIG } from '@/types/facility';
import { LOCKOUT_REASONS } from '@/components/facility/constants';
import { BAY_TYPE_PICKER_OPTIONS, type BayTypeEnum } from '@/utils/bayTypeLabels';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, Camera, Loader2, Sparkles, RotateCw, Maximize2 } from 'lucide-react';
import {
  canonicalDefaultSize,
  deriveOrientation,
  flipDimensions,
  isFlippable,
} from '@/lib/facility/entityGeometry';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * P6 / V-E-2d (file-security pilot) — lift-tags signed-URL adoption.
 *
 * Pre-V-E-2d the `lift-tags` bucket was public and this component called
 * `supabase.storage.from('lift-tags').getPublicUrl(...)` and persisted the
 * resulting URL into `shop_bays.lift_tag_photo_url`. The lift nameplate
 * photos became permanent public URLs at upload time — shop facility data
 * (lift serials, capacity ratings, internal nameplate images) leaked to
 * anyone holding the URL.
 *
 * Post-V-E-2d (see migration
 * 20260519120000_p6_v_e_2d_lift_tags_private.sql) the lift-tags bucket is
 * private with shop-scoped SELECT and manager-gated INSERT/UPDATE/DELETE
 * policies. The column now stores a storage path (not a URL) under the
 * existing `lift_tag_photo_url` field name (the rename to `*_path` is
 * deferred to a future round per V-E-2b's decision). Inline `<img>`
 * rendering goes through `LiftTagThumbnail` which mints a 1-hour signed
 * URL via `createSignedUrl` and caches it through `useQuery` with a
 * 5-minute staleTime — mirrors the canonical single-shot signed-URL
 * pattern used by `AssetDocumentsTab.tsx`.
 *
 * Upload paths use the new shop-scoped shape
 * `{shopId}/{bayId}/{timestamp}.jpg`; the V-E-2d INSERT policy validates
 * the first folder segment as `shop_id` via `is_shop_manager`. Legacy
 * 1-segment paths remain readable to authorized shop members through the
 * SELECT policy's COALESCE-on-foldername JOIN, so no physical-object
 * migration is required.
 */
interface LiftTagThumbnailProps {
  storagePath: string | null | undefined;
}

function LiftTagThumbnail({ storagePath }: LiftTagThumbnailProps) {
  const { data: signedUrl } = useQuery({
    queryKey: ['lift-tag-signed-url', storagePath],
    enabled: !!storagePath,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!storagePath) return null;
      const { data, error } = await supabase.storage
        .from('lift-tags')
        .createSignedUrl(storagePath, 3600);
      if (error) {
        console.warn('[InspectorPanel] lift-tag createSignedUrl failed:', error);
        return null;
      }
      return data?.signedUrl ?? null;
    },
  });

  if (!signedUrl) return null;
  return (
    <div className="mt-2 rounded border overflow-hidden">
      <img src={signedUrl} alt="Lift tag" className="w-full h-auto" />
    </div>
  );
}

// Batch 16-A: the value/label pairs now live in
// `src/utils/bayTypeLabels.ts` as `BAY_TYPE_PICKER_OPTIONS`. This file
// only owns the `dbBayType → entityType` mapping used to drive the
// canvas's `ENTITY_RENDER_CONFIG` after a bay-type change. The mapping
// mirrors the pre-batch behavior 1:1 — both `'flat'` and `'general'`
// resolve to the `flat` entity, same as before.
const BAY_TYPE_TO_ENTITY_TYPE: Record<BayTypeEnum, FacilityEntityType> = {
  '2-post':         'lift_2post',
  '4-post':         'lift_4post',
  'scissor':        'lift_scissor',
  'single-post':    'lift_single_post',
  'alignment':      'alignment',
  'adas':           'adas',
  'alignment-adas': 'alignment_adas',
  'wash':           'wash',
  'inspection':     'inspection',
  'flat':           'flat',
  'general':        'flat',
};

/** Bay types that are actual lifts — show lift metadata fields only for these */
const LIFT_ENTITY_TYPES: FacilityEntityType[] = ['lift_2post', 'lift_4post', 'lift_scissor', 'lift_single_post'];

const LIFT_CAPACITY_SOURCES = ['Tag', 'Manual', 'Manufacturer Spec'] as const;

interface InspectorPanelProps {
  entity: FacilityEntity | null;
  technicians: { id: string; name: string }[];
  onUpdate: (id: string, updates: Partial<FacilityEntity>) => void;
  onDelete: (id: string) => void;
  // Group-membership controls. Both are optional so the existing tests that
  // mount InspectorPanel without these props keep compiling.
  onUngroup?: (id: string, scope: 'self' | 'group') => void;
  groupSiblingCount?: number;
}

export function InspectorPanel({
  entity,
  technicians,
  onUpdate,
  onDelete,
  onUngroup,
  groupSiblingCount = 0,
}: InspectorPanelProps) {
  const liftTagInputRef = useRef<HTMLInputElement>(null);
  const [isScanning, setIsScanning] = useState(false);

  if (!entity) {
    return (
      <div className="p-4 text-center text-xs text-muted-foreground">
        Select an object to inspect
      </div>
    );
  }

  const config = ENTITY_RENDER_CONFIG[entity.entityType];
  // Standalone parking_lot_spots rows (e.g. onboarding parking) only persist
  // position/size from this editor; every shop_bays-only control is hidden.
  const isStandaloneSpot = entity.storageSource === 'parking_lot_spot';

  // Determine current tech select value — treat orphaned assignedTechId as unassigned
  const isAssignedTechActive = entity.assignedTechId 
    ? technicians.some(t => t.id === entity.assignedTechId) 
    : false;
  const techSelectValue = entity.isCommunity
    ? '__community__'
    : (isAssignedTechActive ? entity.assignedTechId : '__unassigned__');

  const handleTechChange = (value: string) => {
    if (value === '__community__') {
      onUpdate(entity.id, { isCommunity: true, assignedTechId: null });
    } else if (value === '__unassigned__') {
      onUpdate(entity.id, { isCommunity: false, assignedTechId: null });
    } else {
      onUpdate(entity.id, { isCommunity: false, assignedTechId: value });
    }
  };

  // Orientation is derived from geometry; Flip / Apply Default Size only
  // change width/height (x/y, id and storage source are untouched) and go
  // through onUpdate like any other edit, so nothing persists until Save.
  const orientation = deriveOrientation(entity.width, entity.height);
  const flipTarget = flipDimensions(entity);
  // Parking and standard service bays have a canonical footprint; specialty
  // bays, zones and doors do not (null → no Apply Default Size).
  const defaultSize = canonicalDefaultSize(entity);
  const isAtDefaultSize =
    !!defaultSize && entity.width === defaultSize.width && entity.height === defaultSize.height;

  const handleBayTypeChange = (dbBayType: string) => {
    const entityType = BAY_TYPE_TO_ENTITY_TYPE[dbBayType as BayTypeEnum];
    if (!entityType) return;
    const newConfig = ENTITY_RENDER_CONFIG[entityType];
    onUpdate(entity.id, {
      entityType,
      dbBayType: dbBayType as FacilityEntity['dbBayType'],
      width: newConfig.defaultWidth,
      height: newConfig.defaultHeight,
    });
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-3 space-y-4">
        {/* Color bar + type label */}
        <div>
          <div
            className="w-full h-1.5 rounded-full mb-3"
            style={{ backgroundColor: config.color }}
          />
          <p className="text-xs font-medium text-muted-foreground">{config.displayLabel}</p>
          {isStandaloneSpot && (
            <p className="text-[11px] text-muted-foreground leading-tight mt-1" data-testid="inspector-standalone-spot-note">
              Parking lot spot — position and size can be edited here.
            </p>
          )}
        </div>

        {/* Name */}
        <div className="space-y-2">
          <Label className="text-xs">Name</Label>
          {isStandaloneSpot ? (
            <Input
              className="h-8 text-xs"
              value={entity.label}
              readOnly
              aria-readonly="true"
              data-testid="inspector-name-readonly"
            />
          ) : (
            <Input
              className="h-8 text-xs"
              value={entity.label}
              onChange={(e) => onUpdate(entity.id, { label: e.target.value })}
            />
          )}
        </div>

        <Separator />

        {/* Bay Type */}
        {entity.entityType !== 'parking_space' && entity.entityType !== 'zone' && entity.entityType !== 'door' && (
          <div className="space-y-2">
            <Label className="text-xs">Bay Type</Label>
            <Select value={entity.dbBayType} onValueChange={handleBayTypeChange}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BAY_TYPE_PICKER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Assigned Technician */}
        {entity.entityType !== 'parking_space' && entity.entityType !== 'zone' && entity.entityType !== 'door' && (
          <div className="space-y-2">
            <Label className="text-xs">Assigned Technician</Label>
            <Select value={techSelectValue} onValueChange={handleTechChange}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned__" className="text-xs">Unassigned</SelectItem>
                <SelectItem value="__community__" className="text-xs">Community Bay</SelectItem>
                {technicians.map(tech => (
                  <SelectItem key={tech.id} value={tech.id} className="text-xs">
                    {tech.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Separator />

        {/* Position */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">X</Label>
            <Input className="h-8 text-xs" value={Math.round(entity.x)} readOnly />
          </div>
          <div>
            <Label className="text-xs">Y</Label>
            <Input className="h-8 text-xs" value={Math.round(entity.y)} readOnly />
          </div>
          <div>
            <Label className="text-xs">Width</Label>
            <Input
              className="h-8 text-xs"
              type="number"
              value={entity.width}
              onChange={(e) => onUpdate(entity.id, { width: Number(e.target.value) || entity.width })}
            />
          </div>
          <div>
            <Label className="text-xs">Height</Label>
            <Input
              className="h-8 text-xs"
              type="number"
              value={entity.height}
              onChange={(e) => onUpdate(entity.id, { height: Number(e.target.value) || entity.height })}
            />
          </div>
        </div>

        {/* Size & orientation — geometry only; content stays upright. */}
        {isFlippable(entity) && (
          <div className="space-y-2" data-testid="inspector-geometry">
            <p className="text-[11px] text-muted-foreground" data-testid="inspector-orientation">
              {entity.width} × {entity.height} · {orientation === 'horizontal' ? 'Horizontal' : orientation === 'vertical' ? 'Vertical' : 'Square'}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs"
              disabled={orientation === 'square'}
              onClick={() => onUpdate(entity.id, flipTarget)}
              data-testid="inspector-flip"
            >
              <RotateCw className="h-3.5 w-3.5 mr-1.5" />
              {orientation === 'vertical' ? 'Flip Horizontal' : orientation === 'horizontal' ? 'Flip Vertical' : 'Flip (square)'}
            </Button>
            {defaultSize && (
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs"
                disabled={isAtDefaultSize}
                onClick={() => onUpdate(entity.id, defaultSize)}
                data-testid="inspector-apply-default-size"
                title={`Resize to the standard ${defaultSize.width} × ${defaultSize.height} size, keeping this object's orientation and position`}
              >
                <Maximize2 className="h-3.5 w-3.5 mr-1.5" />
                Apply Default Size
              </Button>
            )}
          </div>
        )}

        {!isStandaloneSpot && (
        <>
        <Separator />

        {/* Toggles */}
        <div className="flex items-center justify-between">
          <Label className="text-xs">Outdoor</Label>
          <Switch
            checked={entity.isOutdoor}
            onCheckedChange={(v) => onUpdate(entity.id, { isOutdoor: v })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label className="text-xs">Active</Label>
          <Switch
            checked={entity.isActive}
            onCheckedChange={(v) => onUpdate(entity.id, { isActive: v })}
          />
        </div>

        <Separator />

        {/* Bay Lockout */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Locked Out</Label>
            <Switch
              checked={entity.isLockedOut}
              onCheckedChange={(v) =>
                onUpdate(entity.id, {
                  isLockedOut: v,
                  lockoutReason: v ? entity.lockoutReason : null,
                })
              }
            />
          </div>
          {entity.isLockedOut && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Lockout Reason</Label>
              <Select
                value={entity.lockoutReason ?? ''}
                onValueChange={(v) => onUpdate(entity.id, { lockoutReason: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select reason…" />
                </SelectTrigger>
                <SelectContent>
                  {LOCKOUT_REASONS.map(reason => (
                    <SelectItem key={reason} value={reason} className="text-xs">
                      {reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        </>
        )}

        {/* Lift Details — only for true lift bay types */}
        {LIFT_ENTITY_TYPES.includes(entity.entityType) && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Lift Details</p>

              <div>
                <Label className="text-xs">Capacity (lbs)</Label>
                <Input
                  className="h-8 text-xs"
                  type="number"
                  placeholder="e.g. 10000"
                  value={entity.liftCapacityLbs ?? ''}
                  onChange={(e) => onUpdate(entity.id, { liftCapacityLbs: e.target.value ? Number(e.target.value) : null })}
                />
              </div>

              <div>
                <Label className="text-xs">Brand</Label>
                <Input
                  className="h-8 text-xs"
                  placeholder="e.g. BendPak"
                  value={entity.liftBrand ?? ''}
                  onChange={(e) => onUpdate(entity.id, { liftBrand: e.target.value || null })}
                />
              </div>

              <div>
                <Label className="text-xs">Model Number</Label>
                <Input
                  className="h-8 text-xs"
                  placeholder="e.g. XPR-10AS"
                  value={entity.liftModelNumber ?? ''}
                  onChange={(e) => onUpdate(entity.id, { liftModelNumber: e.target.value || null })}
                />
              </div>

              <div>
                <Label className="text-xs">Serial Number</Label>
                <Input
                  className="h-8 text-xs"
                  value={entity.liftSerialNumber ?? ''}
                  onChange={(e) => onUpdate(entity.id, { liftSerialNumber: e.target.value || null })}
                />
              </div>

              <div>
                <Label className="text-xs">Capacity Source</Label>
                <Select
                  value={entity.liftCapacitySource ?? ''}
                  onValueChange={(v) => onUpdate(entity.id, { liftCapacitySource: v || null })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Select source…" />
                  </SelectTrigger>
                  <SelectContent>
                    {LIFT_CAPACITY_SOURCES.map(src => (
                      <SelectItem key={src} value={src} className="text-xs">
                        {src}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scan Lift Tag */}
          <div className="pt-1">
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs"
              disabled={isScanning}
              onClick={() => liftTagInputRef.current?.click()}
            >
              {isScanning ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Scanning...</>
              ) : (
                <><Camera className="h-3.5 w-3.5 mr-1.5" />Scan Lift Tag</>
              )}
            </Button>
            <input
              ref={liftTagInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !entity) return;
                e.target.value = '';
                setIsScanning(true);
                try {
                  // Convert to base64
                  const reader = new FileReader();
                  const base64 = await new Promise<string>((resolve, reject) => {
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                  });

                  // Call parse-lift-tag edge function
                  const { data, error } = await supabase.functions.invoke('parse-lift-tag', {
                    body: { imageBase64: base64 },
                  });
                  if (error) throw error;
                  if (!data?.success) throw new Error(data?.error || 'Failed to parse lift tag');

                  const parsed = data.parsed;
                  const updates: Partial<FacilityEntity> = {};
                  if (parsed.brand) updates.liftBrand = parsed.brand;
                  if (parsed.modelNumber) updates.liftModelNumber = parsed.modelNumber;
                  if (parsed.serialNumber) updates.liftSerialNumber = parsed.serialNumber;
                  if (parsed.capacityLbs) {
                    updates.liftCapacityLbs = parsed.capacityLbs;
                    updates.liftCapacitySource = 'Tag';
                  }
                  onUpdate(entity.id, updates);

                  // V-E-2d: upload to private lift-tags bucket using the
                  // shop-scoped path shape `{shop_id}/{bay_id}/{ts}.jpg`.
                  // The V-E-2d INSERT policy validates `foldername(name)[1]`
                  // as the shop_id via `is_shop_manager`; the column stores
                  // the storage path (not a URL) and the renderer mints a
                  // signed URL on demand via LiftTagThumbnail.
                  const storagePath = `${entity.shopId}/${entity.id}/${Date.now()}.jpg`;
                  const { error: uploadError } = await supabase.storage
                    .from('lift-tags')
                    .upload(storagePath, file, { upsert: true });
                  if (!uploadError) {
                    onUpdate(entity.id, { liftTagPhotoUrl: storagePath });
                  }

                  toast.success('Lift tag scanned — fields updated', {
                    icon: <Sparkles className="h-4 w-4" />,
                  });
                } catch (err: any) {
                  toast.error(err.message || 'Could not scan lift tag');
                } finally {
                  setIsScanning(false);
                }
              }}
            />
            <LiftTagThumbnail storagePath={entity.liftTagPhotoUrl} />
          </div>
        </div>
      </>
    )}

        {!isStandaloneSpot && entity.groupId && onUngroup && (
          <>
            <Separator />
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Group
              </Label>
              <p className="text-[11px] text-muted-foreground leading-tight">
                Part of a row of {groupSiblingCount} item{groupSiblingCount === 1 ? '' : 's'}.
                Click-drag moves the whole group; <kbd className="rounded border px-1 text-[10px]">Shift</kbd>+drag moves just this one to close gaps.
              </p>
              <div className="flex gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => onUngroup(entity.id, 'self')}
                  title="Detach this item from the row (other items stay grouped)"
                >
                  Detach this
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => onUngroup(entity.id, 'group')}
                  title="Break the row into independent items"
                >
                  Ungroup row
                </Button>
              </div>
            </div>
          </>
        )}

        {!isStandaloneSpot && (
          <>
            <Separator />

            <Button
              variant="destructive"
              size="sm"
              className="w-full text-xs"
              onClick={() => onDelete(entity.id)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete
            </Button>
          </>
        )}
      </div>
    </ScrollArea>
  );
}
